//import { useState } from "react";
import { gql, useQuery } from "@apollo/client";
import { ForbiddenError } from "../components/ErrorBoundary";
import {
  StructParams,
  StructureBase,
  getChildType,
  QueryStructParams,
  VDCounts,
} from "../lib/structures";
import type { Timeline } from "../types/Timeline";
import { useEffect } from "react";

interface CrunchWeekDataForChildren {
  tags: {
    all: number;
    unique: number;
    unique_campaign: number;
    duplicate: number;
    unregistered: number;
    target: number;
    total_target: number;
  };
}

// interface Refresh {
//   requestTime: BigInt;
//   executeTime: BigInt;
//   intervalDifference: number;
// }

interface CrunchWeekDataForSelf extends CrunchWeekDataForChildren {
  history: Timeline<number>;
  milestones: Timeline<number>;
  // tagHistory: TimelineTags<number>;
  daily: Timeline<number>;
  dailyCampaign: Timeline<number>;
  //refresh: Refresh;
}

export interface StructureWithCrunchWeek extends StructureWithCrunchWeekBase {
  crunchWeekCanvassing: CrunchWeekDataForSelf;
  parents: StructureBase[];
  children: StructureWithCrunchWeekBase[];
}

interface StructureReturns {
  stats: StructureWithCrunchWeek | undefined;
  loading: boolean;
}

interface StructureWithCrunchWeekBase extends StructureBase {
  crunchWeekCanvassing: CrunchWeekDataForChildren;
  counts: VDCounts;
}

interface CrunchWeekResult {
  structure: StructureWithCrunchWeek;
}

const query = gql`
  query getCrunchWeekData(
    $type: String!
    $code: String!
    $childType: String!
    $filterLabel: String
  ) {
    structure(type: $type, code: $code, filterLabel: $filterLabel) {
      type
      code
      name
      responsiblePersons {
        firstname
        surname
        displayname
      }
      userHasAccess
      structureHasAssignedWards
      counts {
        total
        filtered
      }
      crunchWeekCanvassing {
        tags {
          all
          unique
          unique_campaign
          duplicate
          unregistered
          target
          total_target
        }
        history {
          date
          count
        }
        milestones {
          date
          count
        }
        daily {
          date
          count
        }
        dailyCampaign {
          date
          count
        }
      }
      parents {
        type
        code
        name
        userHasAccess
      }
      children(type: $childType) {
        type
        code
        name
        responsiblePersons {
          firstname
          surname
          displayname
        }
        userHasAccess
        structureHasAssignedWards
        counts {
          total
          filtered
        }
        crunchWeekCanvassing {
          tags {
            all
            unique
            unique_campaign
            duplicate
            unregistered
            target
            total_target
          }
          history {
            date
            count
          }
          milestones {
            date
            count
          }
          daily {
            date
            count
          }
          dailyCampaign {
            date
            count
          }  
        }
      }
    }
  }
`;

const queryWithoutChildren = gql`
  query getCrunchWeekData(
    $type: String!
    $code: String!
    $filterLabel: String
  ) {
    structure(type: $type, code: $code, filterLabel: $filterLabel) {
      type
      code
      name
      responsiblePersons {
        firstname
        surname
        displayname
      }
      userHasAccess
      structureHasAssignedWards
      counts {
        total
        filtered
      }
      crunchWeekCanvassing {
        tags {
          all
          unique
          unique_campaign
          duplicate
          unregistered
          target
          total_target
        }
        history {
          date
          count
        }
        milestones {
          date
          count
        }

        daily {
          date
          count
        }
        dailyCampaign {
          date
          count
        }
      }
      parents {
        type
        code
        name
        userHasAccess
      }
    }
  }
`;

export function useCrunchWeek({
  type,
  code,
  filterLabel,
}: StructParams): StructureReturns {
  //const [refreshStatus, setRefreshStatus] = useState<boolean>(true);

  const childType = getChildType({ type, code });
  const { error, data, refetch, loading } = useQuery<
    CrunchWeekResult,
    QueryStructParams
  >(type === "ward" ? queryWithoutChildren : query, {
    variables: { type, code, childType, filterLabel },
    notifyOnNetworkStatusChange: true,
  });

  useEffect(() => {
    refetch();
    //console.log("graphql: [crunch data]", data);
  }, [filterLabel, refetch]);

  if (error?.graphQLErrors[0]?.extensions?.code === "FORBIDDEN") {
    throw new ForbiddenError("this structure");
  }
  if (error != null) throw error;

  return {
    stats: data?.structure,
    loading: loading,
    //refetch: refetch,
    //setRefreshStatus: setRefreshStatus,
  };
}
